// Thin wrapper around the WorkManager-based upload-job plugin methods
// (NativeFolderSyncPlugin.kt's `startUploadJob`/`getJobStatus`/`listActiveJobs`/
// `cancelJob`) - mirrors how nativeSyncService in nativeProvider.js wraps that
// same plugin's folder-sync lifecycle methods. No separate plugin/registration.
//
// Deliberately observer-shaped: once `startUploadJob` hands a job off, native
// (CmUploadWorker, via WorkManager) does the ENTIRE upload+grouping-kickoff+
// grouping-watch pipeline independent of this page/process - JS never drives
// progress or resumes anything for a job here. `getJobStatus`/`listActiveJobs`
// exist purely to read back what native already knows for display.
import { registerPlugin, Capacitor } from '@capacitor/core';

const NativeFolderSync = registerPlugin('NativeFolderSync');

// Every upload/grouping job (native-worker or JS-driven) mints its jobId the
// same way: `${eventId}-<tag>-${timestamp}-${random}` - centralized here so
// the format only needs to be right in one place.
export function makeUploadJobId(eventId, tag) {
  return `${eventId}-${tag}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const nativeUploadService = {
  // `entries` - [{relativePath, resolveFile?}] for a SAF-backed source (see
  // `resumeSource`) or [{relativePath, file}] for a plain picked/dropped File -
  // for the latter, this reads each File's bytes (base64) up front so native
  // can stage the ORIGINAL durably before this call even returns; there is no
  // other durable place those bytes could come from if the app died a moment
  // later (unlike a SAF entry, which native can always re-resolve from the
  // folder itself via `resumeSource`).
  // Returns the plugin's resolved result (notably `{alreadyActive: true}` when
  // native declined to start a duplicate job for this event - see
  // UploadJobStore.hasActiveJobForEvent's own doc comment) so callers can tell
  // "handed off" apart from "silently not accepted" instead of treating every
  // resolved call as success. `{failed: true}` on a genuine plugin-call
  // failure - logged (not silently swallowed as `{}`, which this used to
  // return) since a caller treating that the same as "handed off" would mark
  // the batch as delivered and drop it from its own pending queue even though
  // no WorkManager job - and hence no compress/upload notification - was ever
  // actually created for it: the photos would just silently never upload.
  async startUploadJob({ jobId, flowKind, eventId, flowMeta, totalFiles, entries, resumeSource }) {
    if (!Capacitor.isNativePlatform()) return {};
    const nativeEntries = await Promise.all((entries || []).map(async (e) => {
      if (resumeSource || !e.file) return { relativePath: e.relativePath };
      const originalBase64 = await blobToBase64(e.file);
      return { relativePath: e.relativePath, originalBase64 };
    }));
    return NativeFolderSync.startUploadJob({
      jobId, flowKind, eventId: String(eventId), flowMeta: flowMeta || {}, totalFiles,
      entries: nativeEntries, resumeSource: resumeSource || null,
    }).catch((err) => {
      console.error('nativeUploadService.startUploadJob: native call failed, job was NOT started', { jobId, flowKind, eventId }, err?.message || err);
      return { failed: true };
    });
  },
  async getJobStatus(jobId) {
    if (!Capacitor.isNativePlatform()) return { found: false };
    try {
      return await NativeFolderSync.getJobStatus({ jobId });
    } catch {
      return { found: false };
    }
  },
  // Polls getJobStatus until the job reaches a terminal outcome ('success' |
  // 'failed' | 'cancelled', set by CmUploadWorker.setOutcome) or disappears
  // (manifest gone - treated the same as not-found, resolves to null), then
  // resolves with the final job summary (or null). Unlike listActiveJobs-based
  // observers (useNativeUploadObserver et al, purely for display on a fixed
  // cadence), this is meant to be *awaited* by a caller that needs to know the
  // real outcome before proceeding - e.g. useLocalAutoSync's engine, which
  // must not resume folder-watching (or treat a batch as delivered) until the
  // native job it just handed the batch off to has actually finished, not
  // merely been enqueued. Mirrors UploadLinkPage.js's own watchNativeJob
  // polling loop (callback-shaped there; this is the promise-shaped twin for
  // callers that need to await the result rather than react to it).
  async waitForJobCompletion(jobId, { intervalMs = 2000, onProgress } = {}) {
    if (!Capacitor.isNativePlatform()) return null;
    for (;;) {
      const res = await this.getJobStatus(jobId);
      if (!res?.found || res.job?.outcome) return res?.job || null;
      onProgress?.(res.job);
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  },
  // Used by EventPage.js/UploadLinkPage.js's observer hook on mount to show
  // live/resuming progress instead of a blank/stale state - purely a read,
  // never re-runs or resumes the job (native never stopped running it).
  async listActiveJobs() {
    if (!Capacitor.isNativePlatform()) return { jobs: [] };
    try {
      const res = await NativeFolderSync.listActiveJobs();
      return res || { jobs: [] };
    } catch {
      return { jobs: [] };
    }
  },
  async cancelJob({ jobId }) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.cancelJob({ jobId }).catch(() => {});
  },

  // --- Legacy headless-WebView upload-job methods (attendee-submit ONLY) ---
  // The attendee in-page "Submit Photos" flow was deliberately left on the
  // older JS-driven model (see resumeUploadJob.js/stagingProvider.js) - short,
  // foreground-only flow, low value from full backgrounding. Nothing new
  // should be built against these; they exist only so that flow keeps working
  // unchanged while every other flow moves to the WorkManager pipeline above.
  async startForegroundUpload({ jobId, flowKind, eventId, totalFiles, label }) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.startForegroundUpload({ jobId, flowKind, eventId, totalFiles, label }).catch(() => {});
  },
  async updateJobProgress({ jobId, completedFiles, totalFiles, phase, lastError }) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.updateJobProgress({ jobId, completedFiles, totalFiles, phase, lastError }).catch(() => {});
  },
  async completeJob({ jobId, outcome }) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.completeJob({ jobId, outcome }).catch(() => {});
  },
  async listIncompleteJobs() {
    if (!Capacitor.isNativePlatform()) return { jobs: [] };
    try {
      const res = await NativeFolderSync.listIncompleteJobs();
      return res || { jobs: [] };
    } catch {
      return { jobs: [] };
    }
  },
  // The legacy pipeline's own cancellation flag (set by the notification's
  // "Cancel" action via FolderSyncForegroundService.requestCancelUploadJob) -
  // polled by resumeUploadJob.js, since these jobs have no `outcome` field in
  // UploadJobStore the way a WorkManager job does. Always false on web/PWA.
  async isUploadJobCancelled(jobId) {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const res = await NativeFolderSync.isUploadJobCancelled({ jobId });
      return Boolean(res?.cancelled);
    } catch {
      return false;
    }
  },
};

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onload = () => {
      const commaIndex = String(reader.result).indexOf(',');
      resolve(commaIndex >= 0 ? reader.result.slice(commaIndex + 1) : reader.result);
    };
    reader.readAsDataURL(blob);
  });
}
